import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Button,
  FormLabel,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { textStyle } from "../../styles/Style";
import {
  validateString,
  validationLength,
  validationType,
} from "../../actions/validations";
import {
  reserveReservationGuestNotesFieldErrors,
  reserveReservationGuestNotesFieldHelperText,
  reserveReservationGuestNotesSectionNotice,
} from "../../../constants/reserveReservationGuestNotesValidationHelperText";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues } from "../../../constants/Types";
import { checkData, deleteItem, updateData } from "../../actions/actionItems";
import { ReservePropertyEnum } from "../../../constants/NavbarList";
import {
  reserveListAvailableHeadingTooltip,
  reserveReservationGuestNotesTooltip,
} from "../../../constants/ReserveTooltipInformation";
import "../../styles/popupStyle.css";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import AlertComponent from "../../subcomponents/AlertComponent";
import useScrollToTop from "../../hooks/useScrollToTop";

const categorySubcategoryKey = (row: {
  Category?: unknown;
  Subcategory?: unknown;
}) =>
  `${String(row.Category ?? "")
    .trim()
    .toLowerCase()}\t${String(row.Subcategory ?? "")
    .trim()
    .toLowerCase()}`;

const ReserveReservationGuestNotes = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [category, setCategory] = useState<string | null>(null);
  const [subcategory, setSubcategory] = useState<string | null>(null);
  const [reservationNotes, setReservationNotes] = useState<string | null>(null);
  const [guestNotes, setGuestNotes] = useState<string | null>(null);
  const [categoryError, setCategoryError] = useState<boolean>(false);
  const [subcategoryError, setSubcategoryError] = useState<boolean>(false);
  const [reservationNotesError, setReservationNotesError] =
    useState<boolean>(false);
  const [guestNotesError, setGuestNotesError] = useState<boolean>(false);
  const [notesMutualExclusiveErr, setNotesMutualExclusiveErr] =
    useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [guestNotesInformation, setGuestNotesInformation] = useState<any[]>([]);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const validateInput = () => {
    setCategoryError(false);
    setSubcategoryError(false);
    setReservationNotesError(false);
    setGuestNotesError(false);
    setNotesMutualExclusiveErr(false);

    let ok = true;
    const categoryMax = validationLength.categoryName;
    const notesMax = validationLength.reservationGuestNotesText;
    const categoryTrim = category?.trim() ?? "";

    if (!categoryTrim.length) {
      setCategoryError(true);
      ok = false;
    } else if (
      !validateString(categoryTrim, categoryMax, validationType.SPACE)
    ) {
      setCategoryError(true);
      ok = false;
    }

    const subcategoryTrim = subcategory?.trim() ?? "";
    if (
      subcategoryTrim.length > 0 &&
      !validateString(subcategoryTrim, categoryMax, validationType.SPACE)
    ) {
      setSubcategoryError(true);
      ok = false;
    }

    const reservationTrim = (reservationNotes ?? "").trim();
    const guestTrim = (guestNotes ?? "").trim();

    if (reservationTrim && guestTrim) {
      setNotesMutualExclusiveErr(true);
      setReservationNotesError(true);
      setGuestNotesError(true);
      ok = false;
    } else {
      if (
        reservationTrim &&
        !validateString(reservationTrim, notesMax, validationType.NAME)
      ) {
        setReservationNotesError(true);
        ok = false;
      }
      if (
        guestTrim &&
        !validateString(guestTrim, notesMax, validationType.NAME)
      ) {
        setGuestNotesError(true);
        ok = false;
      }
    }

    return ok;
  };

  const isDuplicateCategoryRow = (list: any[], excludeId: string | null) => {
    const key = categorySubcategoryKey({
      Category: category,
      Subcategory: subcategory,
    });
    if (key === "\t") {
      return false;
    }
    return (list ?? []).some(
      (item: any) =>
        String(item.id) !== String(excludeId ?? "") &&
        categorySubcategoryKey(item) === key,
    );
  };

  useEffect(() => {
    setGuestNotesInformation(Array.isArray(fieldValues) ? fieldValues : []);
  }, []);

  useEffect(() => {
    data(guestNotesInformation);
    editClicked &&
      setSelectedId(
        checkData(guestNotesInformation, selectedId) ? selectedId : null,
      );
  }, [guestNotesInformation]);

  const updateGuestNotes = () => {
    if (!validateInput()) {
      return;
    }
    if (isDuplicateCategoryRow(guestNotesInformation ?? [], selectedId)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setCategoryError(false);
    setSubcategoryError(false);
    setReservationNotesError(false);
    setGuestNotesError(false);
    setNotesMutualExclusiveErr(false);
    setGuestNotesInformation((prev: any) => {
      return (prev ?? []).map((item: any) => {
        if (String(item.id) === String(selectedId)) {
          const {
            Code: _omitCode,
            Name: _omitName,
            Active: _omitActive,
            ...rest
          } = item;
          return {
            ...rest,
            ...payload,
          };
        }
        return item;
      });
    });
    editGuestNotes(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(
      `${ReservePropertyEnum.reservationGuestNotes} updated successfully`,
    );
    setAlertType("success");
    setOpenAlert(true);
  };

  const addGuestNotes = () => {
    if (!validateInput()) {
      return;
    }
    if (isDuplicateCategoryRow(guestNotesInformation ?? [], null)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setGuestNotesInformation((prev: any) => {
      return updateData(
        Array.isArray(prev) && prev.length
          ? [
              ...prev,
              {
                id: prev.length + 1,
                ...payload,
              },
            ]
          : [
              {
                id: 1,
                ...payload,
              },
            ],
      );
    });
    setLastUpdatedRecord(payload);
    editGuestNotes(null);
    setEditClicked(false);
    setSelectedId(null);
  };

  const editGuestNotes = (list: any) => {
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    if (list == null) {
      setCategory(null);
      setSubcategory(null);
      setReservationNotes(null);
      setGuestNotes(null);
      setCategoryError(false);
      setSubcategoryError(false);
      setReservationNotesError(false);
      setGuestNotesError(false);
      setNotesMutualExclusiveErr(false);
      setEditClicked(false);
      return;
    }
    setCategory(list?.Category ?? null);
    setSubcategory(list?.Subcategory ?? null);
    setReservationNotes(list?.["Reservation notes"] ?? null);
    setGuestNotes(list?.["Guest notes"] ?? null);
    setCategoryError(false);
    setSubcategoryError(false);
    setReservationNotesError(false);
    setGuestNotesError(false);
    setNotesMutualExclusiveErr(false);
  };

  const deleteGuestNotes = (id: number) => {
    setGuestNotesInformation(deleteItem(guestNotesInformation ?? [], id));
  };

  const payload = {
    Category: category,
    Subcategory: subcategory,
    "Reservation notes": reservationNotes,
    "Guest notes": guestNotes,
  };

  const categoryHelperText = categoryError
    ? !(category?.trim() ?? "")
      ? reserveReservationGuestNotesFieldErrors.required
      : reserveReservationGuestNotesFieldErrors.category
    : reserveReservationGuestNotesFieldHelperText.category;

  const subcategoryHelperText = subcategoryError
    ? reserveReservationGuestNotesFieldErrors.subcategory
    : reserveReservationGuestNotesFieldHelperText.subcategory;

  const reservationNotesHelperText = reservationNotesError
    ? notesMutualExclusiveErr
      ? reserveReservationGuestNotesFieldErrors.mutualExclusive
      : reserveReservationGuestNotesFieldErrors.reservationNotes
    : reserveReservationGuestNotesFieldHelperText.reservationNotes;

  const guestNotesHelperText = guestNotesError
    ? notesMutualExclusiveErr
      ? reserveReservationGuestNotesFieldErrors.mutualExclusive
      : reserveReservationGuestNotesFieldErrors.guestNotes
    : reserveReservationGuestNotesFieldHelperText.guestNotes;

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              {ReservePropertyEnum.reservationGuestNotes}
              <Tooltip
                title={tooltipComponent(
                  reserveReservationGuestNotesTooltip.header,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton
                  click={() => {
                    editGuestNotes(null);
                    setSelectedId(null);
                    setEditClicked(false);
                  }}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editGuestNotes(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>

          <Stack direction="column" width="100%" gap="5px">
            <FormLabel component="legend" required>
              Category
              <Tooltip
                title={tooltipComponent(
                  reserveReservationGuestNotesTooltip.category,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="reserveGuestNoteCategory"
              variant="outlined"
              hiddenLabel
              placeholder="Category"
              value={category ?? ""}
              helperText={categoryHelperText}
              error={categoryError}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setCategory(e.target.value);
                setCategoryError(false);
              }}
              fullWidth
              inputProps={{ "aria-label": "Category" }}
            />
          </Stack>

          <Stack direction="column" width="100%" gap="5px">
            <FormLabel component="legend">
              Subcategory
              <Tooltip
                title={tooltipComponent(
                  reserveReservationGuestNotesTooltip.subcategory,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="reserveGuestNoteSubcategory"
              variant="outlined"
              hiddenLabel
              placeholder="Subcategory"
              value={subcategory ?? ""}
              helperText={subcategoryHelperText}
              error={subcategoryError}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setSubcategory(e.target.value);
                setSubcategoryError(false);
              }}
              fullWidth
              inputProps={{ "aria-label": "Subcategory" }}
            />
          </Stack>

          <Typography
            variant="body2"
            sx={{ color: "text.secondary", fontStyle: "italic" }}
          >
            {reserveReservationGuestNotesSectionNotice}
          </Typography>

          <Stack direction="column" width="100%" gap="5px">
            <FormLabel component="legend">
              Reservation notes
              <Tooltip
                title={tooltipComponent(
                  reserveReservationGuestNotesTooltip.reservationNotes,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="reserveGuestNoteReservationNotes"
              variant="outlined"
              hiddenLabel
              placeholder="Reservation notes"
              value={reservationNotes ?? ""}
              helperText={reservationNotesHelperText}
              error={reservationNotesError}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setReservationNotes(e.target.value);
                setReservationNotesError(false);
                setNotesMutualExclusiveErr(false);
              }}
              fullWidth
              multiline
              minRows={3}
              inputProps={{ "aria-label": "Reservation notes" }}
            />
          </Stack>

          <Stack direction="column" width="100%" gap="5px">
            <FormLabel component="legend">
              Guest notes
              <Tooltip
                title={tooltipComponent(
                  reserveReservationGuestNotesTooltip.guestNotes,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="reserveGuestNoteGuestNotes"
              variant="outlined"
              hiddenLabel
              placeholder="Guest notes"
              value={guestNotes ?? ""}
              helperText={guestNotesHelperText}
              error={guestNotesError}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setGuestNotes(e.target.value);
                setGuestNotesError(false);
                setNotesMutualExclusiveErr(false);
              }}
              fullWidth
              multiline
              minRows={3}
              inputProps={{ "aria-label": "Guest notes" }}
            />
          </Stack>

          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={() =>
                selectedId ? updateGuestNotes() : addGuestNotes()
              }
              id="addReserveReservationGuestNotes"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD}{" "}
              {ReservePropertyEnum.reservationGuestNotes}
            </Button>
          </Stack>

          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Typography
              variant="h6"
              component="div"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              {ReservePropertyEnum.reservationGuestNotes} available
              <Tooltip
                title={tooltipComponent(reserveListAvailableHeadingTooltip)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="column" gap="10px">
              {Array.isArray(guestNotesInformation) &&
              guestNotesInformation.length > 0 ? (
                <DraggableItemList
                  itemData={guestNotesInformation}
                  onOrderUpdate={(order) => setGuestNotesInformation(order)}
                  onEdit={(info) => {
                    editGuestNotes(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteGuestNotes(id)}
                  code="Category"
                  name="Subcategory"
                />
              ) : (
                <Typography variant="body2">
                  No {ReservePropertyEnum.reservationGuestNotes} information
                  available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default ReserveReservationGuestNotes;
